import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';

export const MODULE_ACCESS_DENIED_EVENT = 'fattah:module-access-denied';
export const PRE_SWITCH_PATH_KEY = 'fattah:preSwitchPath';

export type ModuleAccessDeniedDetail = {
  message: string;
  module?: ErpModule | null;
};

export function isModuleNotEnabledMessage(message: string | undefined | null): boolean {
  if (!message) return false;
  return /Module [A-Z_]+ is not enabled for this organization/i.test(message);
}

export function parseModuleFromAccessMessage(message: string): ErpModule | null {
  const match = message.match(/Module ([A-Z_]+) is not enabled/i);
  if (!match?.[1]) return null;
  return match[1] as ErpModule;
}

export function isOrgModuleSubscribed(
  module: ErpModule,
  enabledModules: string[],
  accessibleModules?: string[],
): boolean {
  if (accessibleModules?.includes(module)) return true;
  return enabledModules.includes(module);
}

/** Whether the user may open this workspace URL (org subscription + launcher access). */
export function canAccessWorkspacePath(
  pathname: string,
  enabledModules: string[],
  launcherModuleCodes: string[],
  accessibleModules?: string[],
): boolean {
  const pathOnly = pathname.split('?')[0];
  if (!pathOnly || pathOnly === '/') return false;
  if (pathOnly.startsWith('/onboarding') || pathOnly.startsWith('/org/locked')) return true;
  if (isPlatformAdminRoute(pathOnly)) return true;
  if (isGlobalWorkspaceRoute(pathOnly)) return true;

  const pathModule = resolveModuleFromPath(pathOnly);
  if (!pathModule) {
    return pathOnly.startsWith('/org') || pathOnly.startsWith('/workspace');
  }
  if (!isOrgModuleSubscribed(pathModule, enabledModules, accessibleModules)) return false;
  return launcherModuleCodes.includes(pathModule);
}

export function rememberPreSwitchPath(pathname: string, search = ''): void {
  const full = `${pathname}${search}`;
  sessionStorage.setItem(PRE_SWITCH_PATH_KEY, full);
}

export function consumePreSwitchPath(): string | null {
  const value = sessionStorage.getItem(PRE_SWITCH_PATH_KEY);
  sessionStorage.removeItem(PRE_SWITCH_PATH_KEY);
  return value;
}

export function firstAccessibleModuleHome(
  launcherModuleCodes: string[],
): string | null {
  for (const code of launcherModuleCodes) {
    const home = getModuleEntryByErpModule(code)?.homePath;
    if (home) return home;
  }
  return null;
}

export function dispatchModuleAccessDenied(detail: ModuleAccessDeniedDetail): void {
  window.dispatchEvent(new CustomEvent(MODULE_ACCESS_DENIED_EVENT, { detail }));
}
